import { defineComponent as _defineComponent } from 'vue'
import FullCalendar from '@fullcalendar/vue'
import dayGridPlugin from '@fullcalendar/daygrid'
import BKImage from '@/components/AppShared/BKImage.vue'
import { useImage } from '@/shared/composables/image'
import { computed, ref } from 'vue'
import interactionPlugin, { DateClickArg } from '@fullcalendar/interaction'
import { CalendarOptions, EventApi, EventSourceFuncArg, MoreLinkContentArg } from '@fullcalendar/core'
import { eventApi, EventDB } from '@/enitites/event'
import { CalendarEvent } from '../model/types'
import CalendarMobileEventsList from './CalendarMobileEventsList.vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'CalendarMobile',
  setup(__props) {

function getOffsetDate(date: string | Date) {
  const newDate = new Date(date)
  const offsetDate = newDate.getTimezoneOffset()
  return new Date(newDate.getTime() - offsetDate * 60000).toISOString().split('T')[0]
}

const activeMobileList = ref(false)
const modalEventsData = ref<CalendarEvent[]>([])
const clickedDateEl = ref<HTMLElement | null>(null)
const calendar = ref<InstanceType<typeof FullCalendar> | null>(null)

const calendarApi = computed(() => calendar.value?.getApi())

function resetMobileOptions() {
  activeMobileList.value = false
  modalEventsData.value = []
  calendarApi.value?.setOption('dayMaxEvents', 2)
  calendarApi.value?.setOption('moreLinkContent', (args: MoreLinkContentArg) => `+${args.num}`)
  calendarApi.value?.render()
  clickedDateEl.value?.classList.remove('day-clicked')
}

const calendarOptions: CalendarOptions = {
  initialView: 'dayGridMonth',
  plugins: [dayGridPlugin, interactionPlugin],
  headerToolbar: {
    left: '',
    center: 'prev title next',
    end: '',
  },
  events: async (fetchInfo: EventSourceFuncArg) => {
    const start = getOffsetDate(fetchInfo.startStr)
    const end = getOffsetDate(fetchInfo.endStr)

    const data = await eventApi.getEvents({
      pageNumber: 0,
      pageSize: 999,
      start,
      end,
    })

    if (!data || typeof data === 'string') return []
    const publishedEvents = data.filter((event: EventDB) => event.status === 'published')

    return publishedEvents.map((event: EventDB) => ({
      id: event.id,
      title: event.title,
      start: event.dateStart,
      img: event.image,
      optionalEnd: event.dateEnd,
    }))
  },
  eventColor: 'transparent',
  eventBackgroundColor: 'var(--color-gold-800)',
  eventDisplay: 'block',
  dayMaxEvents: true,
  dayHeaderFormat: {
    weekday: 'narrow',
  },
  eventTimeFormat: {
    hour: 'numeric',
    minute: '2-digit',
    meridiem: 'short',
  },
  views: {
    dayGridMonth: {
      dayMaxEvents: 2,
    },
  },
  fixedWeekCount: false,
  dayPopoverFormat: {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  },
  dateClick: (info: DateClickArg) => {
    const { dayEl } = info
    const isOtherDay = dayEl.classList.contains('fc-day-other')
    if (isOtherDay) return
    if (clickedDateEl.value) clickedDateEl.value.classList.remove('day-clicked')
    clickedDateEl.value = dayEl
    modalEventsData.value = []

    const filteredEvents = info.view.calendar.getEvents().filter((event: EventApi) => {
      if (!event.start) return false
      const eventDate = getOffsetDate(event.start)
      const selectedDate = info.dateStr
      return eventDate === selectedDate
    })

    modalEventsData.value = filteredEvents.map((event: EventApi) => {
      return {
        id: event.id,
        title: event.title,
        start: event.start,
        img: event.extendedProps.img,
        optionalEnd: event.extendedProps.optionalEnd,
      }
    })

    calendarApi.value?.setOption('moreLinkContent', () => '')
    calendarApi.value?.setOption('dayMaxEvents', 0)
    calendarApi.value?.render()
    if (!modalEventsData.value.length) {
      resetMobileOptions()
      return
    }
    dayEl.classList.add('day-clicked')
    activeMobileList.value = true
  },
  moreLinkContent: (args: MoreLinkContentArg) => `+${args.num}`,
  moreLinkClick: () => {
    return 'false'
  },
  height: '100%',
  expandRows: true,
}

const { getImageById } = useImage()
const startX = ref(0)
const NECESSARY_RANGE = 50

function touchMoveHandler(event: TouchEvent) {
  const x = event.changedTouches[0].clientX
  const swipeRightRange = x - startX.value
  const swipeLeftRange = startX.value - x
  if (swipeRightRange > NECESSARY_RANGE) calendarApi.value?.prev()

  if (swipeLeftRange > NECESSARY_RANGE) calendarApi.value?.next()
}

function touchStartHandler(event: TouchEvent) {
  startX.value = event.changedTouches[0].clientX
}

return { __sfc: true,getOffsetDate, activeMobileList, modalEventsData, clickedDateEl, calendar, calendarApi, resetMobileOptions, calendarOptions, getImageById, startX, NECESSARY_RANGE, touchMoveHandler, touchStartHandler, FullCalendar, BKImage, CalendarMobileEventsList }
}

})